'use client';

import Link from 'next/link';
import { ExternalLink, Code, Users, Zap } from 'lucide-react';
import { StandardLayout, Button } from '@/app/components';
import { useSite } from '@/lib/site-context';
import '@/app/components/editor/Documentation.scss';

export default function AboutPage() {
  const site = useSite();
  return (
    <StandardLayout>
      <div className="content-wrapper">
        <div className="docs-container">
          {/* Hero Section */}
          <section className="docs-hero">
            <div className="hero-glow"></div>
            <h1>About {site.displayName}</h1>
            <p className="hero-description">
              {site.brand.tagline}
            </p>
          </section>
          {/* What is the site */}
          <section className="docs-section">
            <h2>
              <span className="section-number">01. </span>
              What is {site.displayName}?
            </h2>
            <p className="section-intro">
              {site.displayName} is a web-based platform that brings the power of parametric 3D printing design to your browser. Built on top of the FullControl library, it enables you to:
            </p>
            <div className="docs-grid">
              <div className="docs-card">
                <div className="docs-card-icon">
                  <Code />
                </div>
                <div className="card-content-wrapper">
                  <h3>Code-First Design</h3>
                  <p>Create complex geometries using {site.codeLanguageLabel} instead of traditional CAD tools</p>
                </div>
              </div>
              <div className="docs-card">
                <div className="docs-card-icon">
                  <Zap />
                </div>
                <div className="card-content-wrapper">
                  <h3>Real-time Execution</h3>
                  <p>See your {site.codeLanguageLabel} code transform into 3D toolpaths instantly</p>
                </div>
              </div>
              <div className="docs-card">
                <div className="docs-card-icon">
                  <Users />
                </div>
                <div className="card-content-wrapper">
                  <h3>Community Sharing</h3>
                  <p>Share your designs and learn from others in the community</p>
                </div>
              </div>
            </div>
          </section>
          {/* FullControl Integration */}
          {site.id === 'py' && (
            <section className="docs-section">
              <h2>
                <span className="section-number">02. </span>
                Powered by FullControl
              </h2>
              <p className="section-intro">
                {site.displayName} leverages the{' '}
                <a
                  href="https://github.com/FullControlXYZ/fullcontrol"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-link"
                >
                  FullControl library
                  <ExternalLink size={14} />
                </a>
                , a powerful Python framework for designing 3D printing toolpaths. FullControl
                enables you to define exactly where the printer goes and what it does, giving you
                unprecedented control over the printing process.
              </p>
              <div className="fullcontrol-features">
                <h3>Key FullControl Features:</h3>
                <ul>
                  <li>Direct control over printer movements and extrusion</li>
                  <li>Non-planar printing capabilities</li>
                  <li>Custom infill patterns and structures</li>
                  <li>Parametric design with variables and functions</li>
                  <li>Integration with existing Python scientific libraries</li>
                </ul>
              </div>
            </section>
          )}
          {/* Technology Stack */}
          <section className="docs-section">
            <h2>
              <span className="section-number">{site.id === 'py' ? '03' : '02'}. </span>
              How It Works
            </h2>
            <p className="section-intro">
              {site.id === 'py' ? (
                <>
                  {site.displayName} runs Python directly in your browser using Pyodide, a Python distribution
                  compiled to WebAssembly. This means:
                </>
              ) : (
                <>
                  {site.displayName} runs {site.codeLanguageLabel} directly in your browser with a secure sandbox. This means:
                </>
              )}
            </p>
            <ul>
              <li>No server-side execution required</li>
              {site.id === 'py' ? (
                <>
                  <li>Full Python capabilities in the browser</li>
                  <li>Access to scientific Python libraries</li>
                </>
              ) : (
                <>
                  <li>Full JavaScript capabilities in the browser</li>
                  <li>Modern ES6+ syntax support</li>
                </>
              )}
              <li>Instant feedback and iteration</li>
            </ul>
          </section>
          {/* Getting Started */}
          <section className="docs-section">
            <h2>
              <span className="section-number">{site.id === 'py' ? '04' : '03'}. </span>
              Getting Started
            </h2>
            <p className="section-intro">Ready to start creating? Here's how to begin:</p>
            <ol>
              <li>
                <Link href="/create" className="inline-link">
                  Create a new sketch
                </Link>{' '}
                to start coding
              </li>
              <li>
                Browse the{' '}
                <Link href="/docs" className="inline-link">
                  documentation
                </Link>{' '}
                to learn FullControl basics
              </li>
              <li>
                Explore{' '}
                <Link href="/" className="inline-link">
                  community examples
                </Link>{' '}
                for inspiration
              </li>
              <li>Share your creations with the community</li>
            </ol>
          </section>
          {/* Community */}
          <section className="docs-section">
            <h2>
              <span className="section-number">{site.id === 'py' ? '05' : '04'}. </span>
              Create and Share
            </h2>
            <p className="section-intro">
              Connect with other makers, share your designs, and learn from the community.
              Whether you're new to 3D printing or an experienced engineer, there's a place
              for you in the {site.displayName} community.
            </p>
            <div className="community-actions">
              <Button href="/create" type="primary">
                Start Creating
              </Button>
              <Button href="/docs" type="outline">
                Read the Docs
              </Button>
            </div>
          </section>
        </div>
      </div>
    </StandardLayout>
  );
}
